/**
 * How the `player_name` placeholder key renders a player's name.
 *
 * Values are stable identifiers persisted into board JSON — renaming one breaks
 * every template that already stored it. Add members; do not rename existing
 * ones (same rule as `placeholder-keys.ts`).
 *
 * A closed preset set rather than a token template (`scoreFormat`'s `t1:t2`
 * style): name parts are optional in a way score halves are not. A template
 * like `{surname} {name.i}.{middleName.i}.` renders "Иванов И.." for a player
 * with no отчество, so a token language would need orphan-punctuation cleanup
 * to be correct. Each preset here places its own separators and simply drops
 * absent parts, so every combination is right by construction.
 */
export const PlayerNameFormat = {
  // Surname first.
  SURNAME_NAME_MIDDLE: 'surname_name_middle',
  SURNAME_NAME: 'surname_name',
  SURNAME_NAME_INITIAL: 'surname_name_initial',
  SURNAME_NAME_MIDDLE_INITIALS: 'surname_name_middle_initials',
  SURNAME: 'surname',
  SURNAME_INITIAL_NAME: 'surname_initial_name',
  // Given name first.
  NAME_MIDDLE_SURNAME: 'name_middle_surname',
  NAME_SURNAME: 'name_surname',
  NAME_INITIAL_SURNAME: 'name_initial_surname',
  NAME_MIDDLE_INITIALS_SURNAME: 'name_middle_initials_surname',
  NAME_SURNAME_INITIAL: 'name_surname_initial',
  NAME: 'name',
  NAME_MIDDLE: 'name_middle',
} as const;

export type PlayerNameFormat = (typeof PlayerNameFormat)[keyof typeof PlayerNameFormat];

/**
 * Structured name parts. Carried on `ResolverPlayer` so the format can be
 * applied per placeholder at resolve time — a name flattened to one string at
 * the API boundary cannot be taken apart again (token count does not identify
 * which part is which, and surnames may themselves contain spaces).
 */
export interface PlayerNameParts {
  firstName?: string;
  lastName?: string;
  middleName?: string;
}

/**
 * Surname + given name + отчество — what every existing template rendered
 * before this field existed, so an unset `nameFormat` keeps them byte-identical.
 */
export const DEFAULT_PLAYER_NAME_FORMAT: PlayerNameFormat = PlayerNameFormat.SURNAME_NAME_MIDDLE;

/** Parts joined by spaces; absent parts drop out rather than leaving a gap. */
function join(...tokens: (string | undefined)[]): string {
  return tokens.filter(Boolean).join(' ');
}

/** Initials run together with no space: "И.С.", degrading to "И." alone. */
function glue(...tokens: (string | undefined)[]): string {
  return tokens.filter(Boolean).join('');
}

function initial(value: string | undefined): string | undefined {
  const first = value?.trim()?.[0];
  return first ? `${first.toUpperCase()}.` : undefined;
}

/**
 * One builder per preset. A `Record` keyed by the enum makes a new preset a
 * compile error here rather than a silent fall-through to the default.
 */
const FORMATTERS: Record<PlayerNameFormat, (parts: PlayerNameParts) => string> = {
  [PlayerNameFormat.SURNAME_NAME_MIDDLE]: (p) => join(p.lastName, p.firstName, p.middleName),
  [PlayerNameFormat.SURNAME_NAME]: (p) => join(p.lastName, p.firstName),
  [PlayerNameFormat.SURNAME_NAME_INITIAL]: (p) => join(p.lastName, initial(p.firstName)),
  [PlayerNameFormat.SURNAME_NAME_MIDDLE_INITIALS]: (p) =>
    join(p.lastName, glue(initial(p.firstName), initial(p.middleName))),
  [PlayerNameFormat.SURNAME]: (p) => join(p.lastName),
  [PlayerNameFormat.SURNAME_INITIAL_NAME]: (p) => join(initial(p.lastName), p.firstName),
  [PlayerNameFormat.NAME_MIDDLE_SURNAME]: (p) => join(p.firstName, p.middleName, p.lastName),
  [PlayerNameFormat.NAME_SURNAME]: (p) => join(p.firstName, p.lastName),
  [PlayerNameFormat.NAME_INITIAL_SURNAME]: (p) => join(initial(p.firstName), p.lastName),
  [PlayerNameFormat.NAME_MIDDLE_INITIALS_SURNAME]: (p) =>
    join(glue(initial(p.firstName), initial(p.middleName)), p.lastName),
  [PlayerNameFormat.NAME_SURNAME_INITIAL]: (p) => join(p.firstName, initial(p.lastName)),
  [PlayerNameFormat.NAME]: (p) => join(p.firstName),
  [PlayerNameFormat.NAME_MIDDLE]: (p) => join(p.firstName, p.middleName),
};

const KNOWN_FORMATS: ReadonlySet<string> = new Set(Object.values(PlayerNameFormat));

export function isKnownPlayerNameFormat(value: string | undefined | null): value is PlayerNameFormat {
  return typeof value === 'string' && KNOWN_FORMATS.has(value);
}

/**
 * Renders `parts` in the requested preset. An unset or unrecognized format
 * falls back to the default — a board hand-edited to an unknown value must
 * still render a name, not an empty box.
 */
export function formatPlayerNameParts(parts: PlayerNameParts, format?: string): string {
  const preset = isKnownPlayerNameFormat(format) ? format : DEFAULT_PLAYER_NAME_FORMAT;
  return FORMATTERS[preset](parts).trim();
}

/**
 * Deliberately three different initials: a sample like "Иванов Иван Иванович"
 * collapses "Иванов И." and "Иван И." (and "И. Иван" / "И. Иванов") into
 * near-identical labels, which is exactly the distinction the author is picking
 * between.
 */
export const PLAYER_NAME_FORMAT_SAMPLE: PlayerNameParts = {
  lastName: 'Петров',
  firstName: 'Иван',
  middleName: 'Сергеевич',
};

/**
 * Inspector dropdown options. Each label is the preset applied to the sample,
 * generated through the same formatters the renderer uses — so a label can
 * never drift from what the preset actually produces.
 */
export const PLAYER_NAME_FORMAT_OPTIONS: readonly { value: PlayerNameFormat; label: string }[] =
  Object.values(PlayerNameFormat).map((value) => ({
    value,
    label: formatPlayerNameParts(PLAYER_NAME_FORMAT_SAMPLE, value),
  }));
